import { Box, Button, Typography, Hidden, IconButton, Tooltip, Switch, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import {
  AifApproverAccess,
  enableDisableUser,
  getAIFApproverList,
  getAIFApproverTableListing,
  getAmcApproverAccess,
  UpdateAifApprover,
} from '../../redux-store/actions/userManagement';
import {
  AddAifApproverRequestBody,
  AMCApproverTableListingType,
  AMCCheckerListType,
} from '../../redux-store/types/api-types';
import { ColumnType, DataTable } from '../DataTable';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmationDialog } from '../commonComponents';
import { AccessHeader } from './relationShipManagers';
import { RootStateType } from '../../redux-store/reducers';
import { CursorDirection, USER_ROLES } from '../../utils/constant';

const columns: ColumnType[] = [
  {
    header: 'Name',
    renderCell: (row: AMCCheckerListType) => {
      return (
        <Typography
          variant="h5"
          sx={{
            color: '#61D1D6',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '14px',
            ':hover': {
              color: 'primary.main',
            },
          }}
          component={RouterLink}
          to={{ pathname: 'aif-approver-details', state: { aifApproverId: row?.id } }}>
          {row?.firstName || 'N/A'}
        </Typography>
      );
    },
  },
  {
    header: 'Mobile Number',
    valueGetter: (row: AMCCheckerListType) => row?.phone || 'N/A',
  },
  {
    header: 'Email ID',
    valueGetter: (row: AMCCheckerListType) => row?.email || 'N/A',
  },
];

function aifApprover(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [aifApproverList, setAifApproverList] = useState<AMCCheckerListType[]>([]);

  const [accessForEditAdd, setAccessForEditAdd] = useState(false);

  const {
    page: storePage,
    sortDirection: storeSortDirection,
    rowsPerPage: storeRowsPerPage,
    nextCursor: storeNextCursor,
    prevCursor: storePrevCursor,
    cursorDirection: storeCursorDirection,
    hasNext: storeHasNext,
    hasPrev: storeHasPrev,
    currentCursor: storeCurrentCursor,
    paginationRole,
    // role: storeRole,
  } = useSelector((store: RootStateType) => store.paramsObj);

  const isPaginationRoleIsRM = paginationRole === USER_ROLES?.AMCAPPROVER;

  const [currentPage, setCurrentPage] = useState(isPaginationRoleIsRM ? storePage || 1 : 1);
  const [openDialog, setDialog] = useState<{
    aifApproverRowData: AddAifApproverRequestBody;
  } | null>(null);
  const [hasNext, setHasNext] = useState(isPaginationRoleIsRM ? storeHasNext || false : false);
  const [hasPrev, setHasPrev] = useState(isPaginationRoleIsRM ? storeHasPrev || false : false);
  const [rowsPerPage, setRowsPerPage] = useState(
    isPaginationRoleIsRM ? storeRowsPerPage || 10 : 10
  );
  const [currentCursor, setCurrentCursor] = useState<string | null>(
    isPaginationRoleIsRM ? storeCurrentCursor || null : null
  );
  const [nextCursor, setNextCursor] = useState<string | null>(
    isPaginationRoleIsRM ? storeNextCursor || null : null
  );
  const [prevCursor, setPrevCursor] = useState<string | null>(
    isPaginationRoleIsRM ? storePrevCursor || null : null
  );
  const [cursorDirection, setCursorDirection] = useState<CursorDirection | undefined>(undefined);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);

        const params = {
          limit: rowsPerPage,
          order: 'DESC',
          ...(currentCursor
            ? {
                cursor: currentCursor,
                ...(cursorDirection && { direction: cursorDirection }),
              }
            : {
                cursor: null,
              }),
        };

        const res = (await dispatch(
          getAIFApproverTableListing(params)
        )) as unknown as AMCApproverTableListingType;
        const { amcApproverAccess } = (await dispatch(
          getAmcApproverAccess()
        )) as unknown as AifApproverAccess;
        const { data, nextCursor: nextC, prevCursor: prevC, hasNext, hasPrev } = res || {};
        if (!isComponentAlive) return;

        setNextCursor(nextC || '');
        setPrevCursor(prevC || '');
        setHasNext(hasNext || false);
        setHasPrev(hasPrev || false);
        setLoading(false);

        setAifApproverList(data);
        setAccessForEditAdd(amcApproverAccess);
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isComponentAlive = false;
    };
  }, [currentPage, rowsPerPage, currentCursor, cursorDirection]);

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page
  };

  const handleCursorPageChange = (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
    // If user clicks next
    if (newPage > currentPage - 1 && nextCursor) {
      setCurrentCursor(nextCursor);
      setCursorDirection(CursorDirection.NEXT);
    }
    // If user clicks prev
    else if (newPage < currentPage - 1 && prevCursor) {
      setCurrentCursor(prevCursor);
      setCursorDirection(CursorDirection.PREV);
    }
    // Optionally update currentPage if you want to show the page number
  };

  const AccessViewOrEditCell = [
    {
      header: AccessHeader,
      renderCell: (row: AddAifApproverRequestBody): JSX.Element => {
        return (
          <>
            <Stack
              sx={{
                alignItems: { xs: 'baseline', sm: 'center' },
                '.MuiSwitch-root': {
                  paddingLeft: { xs: '0px', sm: '12px' },
                  width: { xs: '52px', sm: '58px' },
                },
                '.MuiSwitch-switchBase': { paddingLeft: { xs: '0px', sm: '9px' } },
              }}>
              <Switch
                sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                checked={row.isActive}
                onChange={() => setDialog({ aifApproverRowData: row })}
                disabled={!accessForEditAdd}
              />
            </Stack>
          </>
        );
      },
    },
    // {
    //   header: '',
    //   renderCell: (row: AMCCheckerListType): JSX.Element => {
    //     return (
    //       <>
    //         {accessForEditAdd && row.isActive ? (
    //           <Tooltip title="Edit">
    //             <IconButton
    //               component={RouterLink}
    //               to={{ pathname: 'edit-aifapprover', state: { aifApproverId: row.id } }}
    //               sx={{
    //                 ':hover': {
    //                   bgcolor: '#F4FCFC',
    //                 },
    //                 '&.Mui-disabled': {
    //                   cursor: 'pointer',
    //                   pointerEvents: 'all',
    //                 },
    //               }}>
    //               <EditIcon />
    //             </IconButton>
    //           </Tooltip>
    //         ) : (
    //           <Typography
    //             variant="h5"
    //             component={RouterLink}
    //             to={{ pathname: 'edit-aifapprover', state: { aifApproverId: row.id } }}
    //             sx={{
    //               color: '#61D1D6',
    //               textDecoration: 'none',
    //               fontWeight: 500,
    //               fontSize: '14px',
    //               ':hover': {
    //                 color: 'primary.main',
    //               },
    //             }}>
    //             View Details
    //           </Typography>
    //         )}
    //       </>
    //     );
    //   },
    // },
  ];

  return (
    <>
      <Box sx={{ bgcolor: 'common.white', px: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: { xs: 2, sm: 7 },
            pb: { xs: 2, sm: 4 },
          }}>
          <Typography component="h2" sx={{ fontSize: { xs: 15, sm: 25 }, fontWeight: 500 }}>
            List of Approvers
          </Typography>
          {accessForEditAdd && (
            <Hidden only="xs">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  fontSize: 14,
                  mr: 2.5,
                  py: 0.5,
                  px: 3,
                }}
                onClick={() => history.push('add-aif-approver')}>
                Add Checker
              </Button>
            </Hidden>
          )}
          {accessForEditAdd && (
            <Hidden smUp={true}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: 13,
                  // mr: 2.5,
                  py: 0.5,
                  // px: 3,
                  // whiteSpace: 'nowrap',
                }}
                onClick={() => history.push('add-aif-approver')}>
                Add Checker
              </Button>
            </Hidden>
          )}
        </Box>
        {useMemo(
          () => (
            <DataTable
              tableData={aifApproverList}
              tableHeader={[...columns, ...AccessViewOrEditCell]}
              currentPage={currentPage}
              isLoading={isLoading}
              noOfPages={1}
              paginationAction={handlePageChange}
              renderAdditionalRow={true}
              tableBodyCustomStyles={{
                '.MuiTableCell-root': {
                  borderBottom: 'none',
                },
              }}
              tableHeaderCustomStyles={{
                '&:last-child th': { border: 0 },
                '.MuiTableCell-head': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'primary.main',
                },
              }}
              rowCustomStyles={{
                '&:last-child td, &:last-child th': { border: 0 },
                '.MuiTableCell-body ': { color: '#686e72' },
                background: 'transparent',
              }}
              handleCursorPageChange={handleCursorPageChange}
              hasNext={hasNext}
              hasPrev={hasPrev}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              rowsPerPage={rowsPerPage}
            />
          ),
          [aifApproverList, currentPage, isLoading]
        )}
      </Box>
      <ConfirmationDialog
        message={`Are you sure you want to ${
          openDialog?.aifApproverRowData.isActive ? 'disable' : 'enable'
        } the access to this user ?`}
        open={openDialog !== null}
        setOpen={() => setDialog(null)}
        onSave={async () => {
          setDialog(null);
          try {
            if (openDialog) {
              await dispatch(
                enableDisableUser({
                  role: USER_ROLES.AMCAPPROVER,
                  roleId: openDialog?.aifApproverRowData?.id as number,
                  status: openDialog?.aifApproverRowData?.isActive ? 'disable' : 'enable',
                })
              );
              const updatedRowList = aifApproverList?.map((row) => {
                if (row.id === openDialog?.aifApproverRowData.id) {
                  return { ...row, isActive: !openDialog?.aifApproverRowData.isActive };
                }
                return row;
              });
              setAifApproverList(updatedRowList);
            }
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDialog(null)}
      />
    </>
  );
}

export default aifApprover;
